import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, PhoneCall, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { callsAPI } from '../../../api/client';
import { useTenant } from '../../../context/TenantContext';
import { apiError } from '../../../lib/host';
import CallDialog, { prettyNumber } from '../CallDialog';
import { cause, CALL_STATUS, formatDuration, LIVE_STATUSES, timeline, when } from './callModel';
import { CallStatus } from './components/CallBadges';
import { ErrorBanner, PageHeading, StatCards, useBasic } from '../ui';
import AudioPlayer from '../recordings/AudioPlayer';

export default function CallDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useBasic();
  const { can } = useTenant();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [calling, setCalling] = useState(false);

  const load = useCallback(() => {
    callsAPI.get(id).then((res) => { setData(res.data); setError(''); })
      .catch((err) => setError(apiError(err, 'Could not load this call')));
  }, [id]);
  useEffect(() => { setData(null); load(); }, [load]);

  // While the call is still connecting, refresh so the status and timeline update by themselves.
  const live = data && LIVE_STATUSES.includes(data.call.status);
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [live, load]);

  const back = <button className="secondary-action" onClick={() => navigate('/calls')}><ArrowLeft size={14} />Call history</button>;
  if (!data) return <section className="page-view"><PageHeading title="Call details" subtitle={error ? '' : 'Loading…'}>{back}</PageHeading><ErrorBanner message={error} /></section>;

  const { call, history } = data;
  const who = call.contact_name || prettyNumber(call.other_number);
  const inbound = call.direction === 'inbound';
  const isVoicemail = call.status === 'voicemail';

  return (
    <section className="page-view">
      <PageHeading title={who} subtitle={`${inbound ? 'Incoming' : 'Outgoing'} call · ${when(call.started_at)}`}>
        {back}
        {can('calls.make') && <button className="primary-action" onClick={() => setCalling(true)}><PhoneCall size={14} />Call back</button>}
      </PageHeading>
      <ErrorBanner message={error} />

      <StatCards stats={[
        ['Direction', inbound ? 'Incoming' : 'Outgoing', inbound ? 'Customer called you' : 'You called the customer'],
        ['Outcome', CALL_STATUS[call.status]?.[0] || call.status, cause(call.hangup_cause) || ' '],
        ['Duration', formatDuration(call.duration_seconds), call.answered_at ? 'Talk time' : 'Not connected'],
        ['Team member', call.user_name || '—', inbound ? 'Answered by the routing' : 'Placed the call'],
      ]} />

      <div className="call-detail-grid">
        <section className="panel page-info-card">
          <div className="panel-heading"><h2>What happened</h2><CallStatus status={call.status} /></div>
          <ol className="call-timeline">
            {timeline(call).map(([text, at, color], i) => (
              <li key={i} style={{ '--dot': color }}><b>{text}</b>{at}</li>
            ))}
          </ol>
          {call.recording_id && (
            <div className="call-recording">
              <div className="panel-heading"><h2>{isVoicemail ? 'Voicemail' : 'Recording'}</h2></div>
              <AudioPlayer recordingId={call.recording_id} duration={call.duration_seconds} label={isVoicemail ? 'Voicemail' : 'Call recording'} />
            </div>
          )}
        </section>

        <section className="panel page-info-card">
          <div className="panel-heading"><h2>Call details</h2></div>
          <dl className="detail-list">
            <dt>Customer</dt><dd>{call.contact_name || 'Not in contacts'}</dd>
            <dt>Customer number</dt><dd>{prettyNumber(call.other_number)}</dd>
            <dt>Company number</dt><dd>{prettyNumber(call.company_number)}</dd>
            <dt>Started</dt><dd>{when(call.started_at)}</dd>
            <dt>Answered</dt><dd>{when(call.answered_at) || '—'}</dd>
            <dt>Ended</dt><dd>{when(call.ended_at) || '—'}</dd>
            <dt>Call ID</dt><dd>#{call.id}</dd>
          </dl>
        </section>
      </div>

      <section className="panel page-table-panel" style={{ marginTop: 15 }}>
        <div className="panel-heading"><div><h2>Other calls with {who}</h2><small className="table-subtitle">Latest {history.length} call{history.length === 1 ? '' : 's'} with this number</small></div></div>
        <div className="table-scroll">
          <table className="full-page-table">
            <thead><tr><th>When</th><th>Direction</th><th>Team member</th><th>Duration</th><th>Status</th></tr></thead>
            <tbody>
              {history.map((c) => (
                <tr key={c.id}>
                  <td><button className="link-button" onClick={() => navigate(`/calls/${c.id}`)}>{when(c.started_at)}</button></td>
                  <td><span className={c.direction === 'inbound' ? 'type-incoming' : 'type-outgoing'}>
                    {c.direction === 'inbound' ? <PhoneIncoming size={12} /> : <PhoneOutgoing size={12} />}{c.direction === 'inbound' ? 'Incoming' : 'Outgoing'}
                  </span></td>
                  <td>{c.user_name || '—'}</td>
                  <td>{formatDuration(c.duration_seconds)}</td>
                  <td><CallStatus status={c.status} /></td>
                </tr>
              ))}
              {history.length === 0 && <tr><td colSpan={5} className="empty-state">This is the only call with this number so far.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      {calling && (
        <CallDialog number={call.other_number} contact={call.contact_id ? { id: call.contact_id, name: call.contact_name, phone: call.other_number } : null}
          onClose={() => setCalling(false)}
          onPlaced={(c) => { setCalling(false); notify(`Calling your phone now. Answer it to be connected to ${who}.`); navigate(`/calls/${c.id}`); }} />
      )}
    </section>
  );
}
